import { Game } from 'chessboard/model/game/types';
import type JCE from '@chesscom/js-chess-engine';
import type { Data } from 'src/utils/svelte/data';
import data from 'src/utils/svelte/data';
import { isMovesRow } from '../config/types';
import type { MoveListComponent } from '../config/types';
import {
  getCommentsForNode,
  nodeHasMultipleOrNestedVariations,
  nodeIsFirstInLineAndHasVariations,
} from './node-line-utils';
import { getLineWithVariationsInlined } from './get-line-with-variations-inlined';
import type { RenderKeys } from './get-row-render-key';
import { getRowRenderKey } from './get-row-render-key';

export type MoveListProcessingOptions = {
  ignoreVariations?: boolean | JCE.Ids;
  ignoreComments?: boolean;
};

export type VariationProcessingOptions = {
  ignoreFirstNodeVariations?: boolean;
  initComment?: string;
  ignoreComments?: boolean;
};

type AppendRowFn = (row: Omit<MoveListComponent.Row, 'renderKey'>) => void;

/**
 * The max variation indentation depth before switching to only inlining variations.
 */
const MAX_DEPTH = 8;

/**
 * Transforms the array of lines into a flat shape that can be used to render the move list.
 */
export function moveListToFlatTree(
  lines: Game.Line[],
  { ignoreComments, ignoreVariations }: MoveListProcessingOptions = {},
): Data<MoveListComponent.Row>[] {
  const rows: MoveListComponent.Row[] = [];
  const rowRenderKeys: RenderKeys = {};

  const appendRow: AppendRowFn = (row) => {
    rows.push({ ...row, renderKey: getRowRenderKey(row, rowRenderKeys) });
  };

  const mainLine = lines[0];
  let accLine: MoveListComponent.MoveListNode[] = [];

  if (mainLine.initComment !== undefined && !ignoreComments) {
    appendRow({
      content: {
        comment: mainLine.initComment,
        node: mainLine[0] ?? null,
        lineNumber: 0,
        isBeforeComment: true,
      },
      depth: 0,
    });
  }

  mainLine.forEach((node) => {
    accLine.push({ node });

    if (node.comment !== undefined && !ignoreComments) {
      appendRow({ content: accLine, depth: 0 });
      appendRow({ content: { comment: node.comment, node }, depth: 0 });
      accLine = [];
    } else if (node.color === Game.ColorsAsNumbers.Black) {
      appendRow({ content: accLine, depth: 0 });
      accLine = [];
    }

    if (!node.lines || shouldIgnoreVariations(node.ids, ignoreVariations)) {
      return;
    }

    // append main line row when a variation is encountered
    // splits main line plys if variation is on a white move
    if (accLine.length > 0) {
      appendRow({ content: accLine, depth: 0 });
      accLine = [];
    }

    // append variation rows
    node.lines.forEach((lineIndex) => {
      appendVariationRows(lines[lineIndex], 1, appendRow, lines, {
        initComment: lines[lineIndex].initComment,
        ignoreComments,
      });
    });
  });

  // append any remaining main line nodes
  if (accLine.length > 0) {
    appendRow({ content: accLine, depth: 0 });
  }

  return getRowsWithColors(rows).map((row) => data(row));
}

/**
 * Gets the supplied rows with alternating main line colors.
 * Variation blocks and mainline comments match the color of the relevant main line row.
 */
function getRowsWithColors(rows: MoveListComponent.Row[]) {
  let currentColor: MoveListComponent.Row['rowBgColor'];

  return rows.map((row) => {
    if (isMovesRow(row) && row.depth === 0) {
      currentColor = currentColor === 'light-row' ? 'dark-row' : 'light-row';
    }

    return {
      ...row,
      rowBgColor: currentColor ?? 'light-row',
    };
  });
}

/**
 * Traverses the variation tree and calls `appendRow` for each variation line.
 * * variations off of main line are never inline
 * * sub variations:
 *   * if a variation line has multiple variations on the same node, branch and increase depth
 *   * if a variation line only has no more than one variation on a node...
 *     * if any of the sub variations have additional levels, branch and increase depth
 *     * if all of the sub variaions are single level, inline
 */
function appendVariationRows(
  variationLine: Game.Move.Node[],
  depth: number,
  appendRow: AppendRowFn,
  lines: JCE.Line[],
  options: VariationProcessingOptions = {},
) {
  if (depth >= MAX_DEPTH) {
    appendRow({
      content: getLineWithVariationsInlined(variationLine, lines, options),
      depth,
    });
    return;
  }

  if (nodeIsFirstInLineAndHasVariations(variationLine[0]) && !options.ignoreFirstNodeVariations) {
    // If there is a variation on the first node of a variation,
    // we want to render them as siblings at the current depth.
    // i.e: 1. e4 (1. e3 (1. d4)) should render equivalently to 1. e4 (1. e3) (1. d4)
    appendSubVariationsAsSiblings(variationLine, depth, appendRow, lines, {
      ignoreComments: options.ignoreComments,
    });
    return;
  }

  let accVariationLine: MoveListComponent.MoveListNode[] = [];
  for (let i = 0; i < variationLine.length; i++) {
    const node = variationLine[i];

    if (i === 0 || !node.lines) {
      const { beforeComment, afterComment } = getCommentsForNode(
        node,
        options.initComment,
        options.ignoreComments,
      );
      accVariationLine.push({
        node,
        beforeComment,
        afterComment,
      });
    } else if (node.lines) {
      if (nodeHasMultipleOrNestedVariations(node, lines)) {
        // append the processed variation nodes when a sub-variation is encountered
        if (accVariationLine.length > 0) {
          appendRow({ content: accVariationLine, depth });
          accVariationLine = [];
        }

        // continue the line at the next depth, with sub-variations as siblings
        appendSubVariationsAsSiblings(variationLine.slice(i), depth + 1, appendRow, lines, {
          ignoreComments: options.ignoreComments,
        });

        // break out of the loop since the variation is continued in a child row
        break;
      }

      // Otherwise inline the sub-variation
      const subVariationLine = lines[node.lines[0]];
      const inlineVariation = getLineWithVariationsInlined(subVariationLine, lines, {
        ignoreComments: options.ignoreComments,
        initComment: subVariationLine.initComment,
      });

      const { beforeComment, afterComment } = getCommentsForNode(
        node,
        options.initComment,
        options.ignoreComments,
      );
      accVariationLine.push({
        node,
        inlineVariations: [inlineVariation],
        beforeComment,
        afterComment,
      });
    }
  }

  // append the processed variation nodes
  if (accVariationLine.length > 0) {
    appendRow({ content: accVariationLine, depth });
  }
}

/**
 * Appends variation line with its first node's sub-variations as siblings at the same depth.
 */
function appendSubVariationsAsSiblings(
  variationLine: Game.Move.Node[],
  depth: number,
  appendRow: AppendRowFn,
  lines: JCE.Line[],
  { ignoreComments }: { ignoreComments?: boolean } = {},
) {
  appendVariationRows(variationLine, depth, appendRow, lines, {
    ignoreFirstNodeVariations: true,
    ignoreComments,
  });

  // append the sub-variations at the same depth
  variationLine[0]?.lines?.forEach((subVariationLineIndex) => {
    appendVariationRows(lines[subVariationLineIndex], depth, appendRow, lines, {
      initComment: lines[subVariationLineIndex].initComment,
      ignoreComments,
    });
  });
}

/**
 * determines if the variations should be ignored for a particular node
 */
function shouldIgnoreVariations(
  ids: JCE.Ids,
  ignoreVariations: MoveListProcessingOptions['ignoreVariations'],
) {
  if (ignoreVariations === true) {
    return true;
  }

  return (
    typeof ignoreVariations === 'object' &&
    (ignoreVariations.line !== ids.line || ignoreVariations.move !== ids.move)
  );
}
